import { Component, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppService } from '../../../services/app.service';
import { Product } from '../../../models/product.model';
import { ProductModel } from '../../../models/product-model.model';
import { SpecificationValue } from '../../../models/specification-value.model';
import { Specification } from '../../../models/specification.model';
import { CategoryDetails } from '../../../models/category-details.model';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import Swal from "sweetalert2";

@Component({
  selector: 'app-admin-products',
  templateUrl: './admin-products.component.html',
  styleUrls: ['./admin-products.component.scss']
})
export class AdminProductsComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();
  products: Product[] = [];
  categories: CategoryDetails[] = [];
  specifications: Specification[] = [];

  isModalOpen = false;
  isEditMode = false;
  currentProductId: number | null = null;
  isLoading = false;

  // View modal
  isViewModalOpen = false;
  viewProduct: Product | null = null;

  // Product image files
  productImageFiles: { file?: File; preview: string; base64?: string; imageDetailsId?: number; imagePath?: string; imageType?: string; }[] = [];

  productForm = new UntypedFormGroup({
    productName: new UntypedFormControl("", [Validators.required, Validators.nullValidator]),
    productCode: new UntypedFormControl("", [Validators.required, Validators.nullValidator]),
    description: new UntypedFormControl("", [Validators.nullValidator]),
    price: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
    availability: new UntypedFormControl("IN_STOCK", [Validators.required]),
    totalQuantity: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
    categoryId: new UntypedFormControl(null, [Validators.required, Validators.nullValidator]),
    models: new UntypedFormArray([])
  });

  productFormErrors = {
    productName: '',
    productCode: '',
    categoryId: '',
    price: '',
    totalQuantity: ''
  };

  constructor(
    private appService: AppService,
    private toaster: ToastrService,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.loadProducts();
    this.loadCategories();
    this.loadSpecifications();
  }

  ngAfterViewInit(): void {
    if (window.history.state && window.history.state.openAddProductModal) {
      setTimeout(() => {
        this.openAddModal();
      }, 300); // Slight delay to ensure UI is ready
    }
  }

  // --- Form Array Getters ---
  get modelsArray(): UntypedFormArray {
    return this.productForm.get('models') as UntypedFormArray;
  }

  getSpecsArray(modelIndex: number): UntypedFormArray {
    return this.modelsArray.at(modelIndex).get('specificationValues') as UntypedFormArray;
  }

  // --- Form Array Builders ---
  createModelGroup(modelData?: ProductModel): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(modelData ? modelData.id : 0),
      modelName: new UntypedFormControl(modelData ? modelData.modelName : '', [Validators.required]),
      powerSource: new UntypedFormControl(modelData ? modelData.powerSource : ''),
      countryOfOrigin: new UntypedFormControl(modelData ? modelData.countryOfOrigin : ''),
      description: new UntypedFormControl(modelData ? modelData.description : ''),
      productId: new UntypedFormControl(modelData ? modelData.productId : 0),
      specificationValues: new UntypedFormArray([]),
    });
  }

  createSpecGroup(specData?: SpecificationValue): UntypedFormGroup {
    return new UntypedFormGroup({
      id: new UntypedFormControl(specData ? specData.id : 0),
      productModelId: new UntypedFormControl(specData ? specData.productModelId : 0),
      specificationId: new UntypedFormControl(specData ? specData.specificationId : 0),
      value: new UntypedFormControl(specData ? specData.value : '', [Validators.required]),
      specification: new UntypedFormGroup({
        id: new UntypedFormControl(specData && specData.specification ? specData.specification.id : 0),
        specName: new UntypedFormControl(specData && specData.specification ? specData.specification.specName : '', [Validators.required]),
        unit: new UntypedFormControl(specData && specData.specification ? specData.specification.unit : ''),
        type: new UntypedFormControl(specData && specData.specification ? specData.specification.type : 'GENERAL')
      })
    });
  }

  // --- Form Array Actions ---
  addModel(): void {
    this.modelsArray.push(this.createModelGroup());
  }

  removeModel(index: number): void {
    this.modelsArray.removeAt(index);
  }

  addSpec(modelIndex: number): void {
    this.getSpecsArray(modelIndex).push(this.createSpecGroup());
  }

  removeSpec(modelIndex: number, specIndex: number): void {
    const specArray = this.getSpecsArray(modelIndex);
    const specGroup = specArray.at(specIndex);
    const specId = specGroup.get('id')?.value;

    if (specId && specId > 0) {
      // It's an existing spec in the backend, ask for confirmation then delete
      Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the specification from the model.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!"
      }).then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.appService.deleteProductSpecification(specId).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.spinner.hide();
              specArray.removeAt(specIndex);
              this.toaster.success('Specification deleted successfully');
            },
            error: (err) => {
              this.spinner.hide();
              this.toaster.error('Failed to delete specification');
              console.error('Delete specification error', err);
            }
          });
        }
      });
    } else {
      // It's a newly added row that hasn't been saved yet, just remove from array
      specArray.removeAt(specIndex);
    }
  }

  // --- Product Image Handling ---
  onProductImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file format (jpg, jpeg, png)
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        this.toaster.error('Invalid file format. Please upload JPG or PNG.');
        input.value = '';
        return;
      }

      // Validate file size (Max 2MB)
      const maxSizeInBytes = 2 * 1024 * 1024;
      if (file.size > maxSizeInBytes) {
        this.toaster.error('File size exceeds 2MB limit. Please upload a smaller image.');
        input.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        const preview = reader.result as string;
        this.productImageFiles.push({ file, preview, base64 });
      };
      reader.readAsDataURL(file);
      // Reset the input so the same file can be selected again
      input.value = '';
    }
  }

  removeProductImage(index: number): void {
    const img = this.productImageFiles[index];
    if (img.imageDetailsId) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will permanently delete the image from the server.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!"
      }).then((result) => {
        if (result.isConfirmed) {
          this.spinner.show();
          this.appService.deleteProductImage(img.imageDetailsId!).pipe(takeUntil(this.destroy$)).subscribe({
            next: () => {
              this.spinner.hide();
              this.productImageFiles.splice(index, 1);
              this.toaster.success('Image deleted successfully');
            },
            error: (err) => {
              this.spinner.hide();
              this.toaster.error('Failed to delete image');
              console.error('Delete image error', err);
            }
          });
        }
      });
    } else {
      this.productImageFiles.splice(index, 1);
    }
  }

  getProductImageUrl(imageId: number): string {
    return this.appService.getProductImageUrl(imageId);
  }


  // --- API & UI Logic ---
  loadProducts(): void {
    this.isLoading = true;
    this.appService.getAllProducts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.products = data;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error fetching products', err);
          this.toaster.error('Failed to load products');
          this.isLoading = false;
        }
      });
  }

  loadCategories(): void {
    this.appService.getAllCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.categories = data,
        error: (err) => console.error('Error loading categories', err)
      });
  }

  loadSpecifications(): void {
    this.appService.getAllSpecifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => this.specifications = data,
        error: (err) => console.error('Error loading specifications', err)
      });
  }

  onSpecSelected(modelIndex: number, specIndex: number): void {
    const specGroup = this.getSpecsArray(modelIndex).at(specIndex);
    const id = specGroup.get('specificationId')?.value;
    const selectedSpec = this.specifications.find(s => s.id === id);
    if (selectedSpec && specGroup) {
      (specGroup.get('specification') as UntypedFormGroup).patchValue({
        id: selectedSpec.id,
        specName: selectedSpec.specName,
        unit: selectedSpec.unit,
        type: selectedSpec.type
      });
    }
  }

  openAddModal(): void {
    this.isEditMode = false;
    this.currentProductId = null;
    this.productForm.reset({ price: null, totalQuantity: null, availability: 'IN_STOCK' });
    this.modelsArray.clear();
    this.productImageFiles = [];
    // Start with 1 empty model by default when adding a new product
    this.addModel();
    this.isModalOpen = true;
  }

  openEditModal(product: Product): void {
    this.isEditMode = true;
    this.currentProductId = product.productId;
    this.modelsArray.clear();
    this.productImageFiles = [];

    // Rebuild FormArray from product data
    if (product.models && product.models.length > 0) {
      product.models.forEach((model, mIndex) => {
        const modelGroup = this.createModelGroup(model);
        const specArray = modelGroup.get('specificationValues') as UntypedFormArray;

        if (model.specificationValues && model.specificationValues.length > 0) {
          model.specificationValues.forEach(spec => {
            specArray.push(this.createSpecGroup(spec));
          });
        }
        this.modelsArray.push(modelGroup);
      });
    }

    if (product.imageDetails && product.imageDetails.length > 0) {
      product.imageDetails.forEach(img => {
        this.productImageFiles.push({
          imageDetailsId: img.imageDetailsId,
          imagePath: img.imagePath,
          imageType: img.imageType,
          preview: this.getProductImageUrl(img.imageDetailsId)
        });
      });
    }

    this.productForm.patchValue({
      productName: product.productName,
      productCode: product.productCode,
      description: product.description,
      price: product.price,
      availability: product.availability,
      totalQuantity: product.totalQuantity,
      categoryId: product.categoryId
    });
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.productImageFiles = [];
  }

  // --- View Modal ---
  openViewModal(product: Product): void {
    this.spinner.show();
    this.appService.getProductById(product.productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.viewProduct = data;
          this.isViewModalOpen = true;
          this.spinner.hide();
        },
        error: (err) => {
          console.error('Error fetching product details', err);
          this.toaster.error('Failed to load product details');
          this.spinner.hide();
        }
      });
  }

  closeViewModal(): void {
    this.isViewModalOpen = false;
    this.viewProduct = null;
  }

  saveProduct(): void {
    this.formValidation();
  }

  formValidation(): void {
    let hasError = false;
    Object.keys(this.productFormErrors).forEach(key => {
      // @ts-ignore
      this.productFormErrors[key] = '';
    });

    if (this.productForm.get('productName')?.invalid) {
      this.productFormErrors.productName = '*Product name is required';
      hasError = true;
    }

    if (this.productForm.get('productCode')?.invalid) {
      this.productFormErrors.productCode = '*Product code is required';
      hasError = true;
    }

    if (this.productForm.get('categoryId')?.invalid || !this.productForm.get('categoryId')?.value) {
      this.productFormErrors.categoryId = '*Category is required';
      hasError = true;
    }

    if (this.productForm.get('price')?.invalid) {
      this.productFormErrors.price = '*Valid price is required';
      hasError = true;
    }

    if (this.productForm.get('totalQuantity')?.invalid) {
      this.productFormErrors.totalQuantity = '*Valid quantity is required';
      hasError = true;
    }


    if (!hasError) {
      this.submitProductDetails();
    }
  }

  submitProductDetails(): void {
    this.spinner.show();
    const formData = this.productForm.value;

    // Build product-level imageDetails from selected files
    const imageDetails = this.productImageFiles.map((img, i) => {
      if (img.imageDetailsId) {
        return {
          imageDetailsId: img.imageDetailsId,
          imageType: img.imageType || 'image/jpeg',
          imagePath: img.imagePath || '',
          primary: i === 0
        };
      }
      return {
        imageDetailsId: 0,
        imageType: img.file?.type || '',
        imageData: img.base64,
        primary: i === 0
      };
    });

    const cleanModels = formData.models ? formData.models.map((m: any) => ({
      id: m.id || 0,
      modelName: m.modelName,
      powerSource: m.powerSource,
      countryOfOrigin: m.countryOfOrigin,
      description: m.description,
      specificationValues: m.specificationValues ? m.specificationValues.map((sv: any) => ({
        id: sv.id || 0,
        specificationId: sv.specificationId,
        value: sv.value
      })) : []
    })) : [];

    // Construct the payload matching the exactly requested DTO
    const productPayload: any = {
      productId: this.isEditMode && this.currentProductId ? this.currentProductId : 0,
      productName: formData.productName,
      productCode: formData.productCode,
      description: formData.description,
      price: formData.price,
      shopId: 1,
      availability: formData.availability,
      totalQuantity: formData.totalQuantity,
      categoryId: formData.categoryId,
      models: cleanModels,
      imageDetails: imageDetails
    };

    if (this.isEditMode && this.currentProductId) {
      productPayload.productId = this.currentProductId;

      this.appService.updateProduct(productPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.successPopup('Product Updated Successfully');
          },
          error: (err) => {
            console.error('Update error', err);
            this.spinner.hide();
            this.errorPopup('Failed to update product');
          }
        });
    } else {
      productPayload.productId = 0; // Backend generates ID

      this.appService.addProduct(productPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.spinner.hide();
            this.successPopup('Product Added Successfully');
          },
          error: (err) => {
            console.error('Save error', err);
            this.spinner.hide();
            this.errorPopup('Failed to add product');
          }
        });
    }
  }

  toggleProductAvailability(product: Product, event: any): void {
    const isChecked = event.target.checked;
    const newStatus = isChecked ? 'IN_STOCK' : 'OUT_OF_STOCK';
    this.spinner.show();

    // Create an updated payload
    const updatePayload = {
      ...product,
      availability: newStatus
    };

    this.appService.updateProductAvailability(updatePayload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.spinner.hide();
          this.toaster.success(`Product is now ${newStatus}`);
          // Update local state
          product.availability = newStatus;
        },
        error: (err) => {
          console.error('Update product status error:', err);
          this.spinner.hide();
          this.errorPopup('Failed to update product status');
          // Revert toggle state in UI
          event.target.checked = !isChecked;
        }
      });
  }

  successPopup(msg: string) {
    this.closeModal();
    this.loadProducts();
    Swal.fire({
      icon: "success",
      title: msg
    });
  }

  errorPopup(msg: string = "Something went wrong, try again later") {
    Swal.fire({
      icon: "error",
      title: msg
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
